import React, { useState, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { Services } from '@/services/services';
import { Translates } from '@/services/repository/i18n';

export interface FormBase {
  _general: string;
}

export interface NewPasswordFormData extends FormBase {
  email: string;
  password: string;
  passwordRepeat: string;
}

export interface FormError {
  message: string;
}

export type FormErrors<T> = {
  [K in keyof T]: FormError;
}

export const NewPasswordForm = ({
  services,
  onSubmit,
  serverErrors,
  form,
}: {
  services: Services;
  onSubmit: (
    data: NewPasswordFormData,
    resetForm: () => void
  ) => Promise<void>;
  serverErrors: FormErrors<NewPasswordFormData> | undefined;
  form: UseFormReturn<NewPasswordFormData>;
}) => {
  const [translates, setTranslates] = useState<Translates>();
  const {
    setFocus,
    register,
    setError,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    services.i18n.get('main').then(setTranslates);
  }, []);

  useEffect(() => {
    if (serverErrors === undefined) {
      return;
    }
    for (let i in serverErrors) {
      const key = i as keyof NewPasswordFormData;
      setError(key, {
        type: 'server',
        message: translates?.for(serverErrors[key]!.message),
      });
      setFocus(key);
    }
  }, [translates, serverErrors]);

  const handleFormSubmit: SubmitHandler<NewPasswordFormData> = (data) => {
    if (data.password !== data.passwordRepeat) {
      setError('passwordRepeat', {
        type: 'validate',
        message: translates?.for('forms.resetpassword.errors.passwordMismatch'),
      });
      return;
    }
    onSubmit(data, reset);
  };

  if (translates === undefined) {
    return <div className='spinner'></div>
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>

      {/* Email Field */}
      <div className="form-field">
        <label htmlFor="email">{translates.for('forms.resetpassword.fields.email.title')}:</label>
        <input
          type="email"
          id="email"
          readOnly={true}
          placeholder={translates.for('forms.resetpassword.fields.email.placeholder')}
          {...register('email', {
            required: translates.for('forms.resetpassword.errors.fields.email.required'),
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              message: translates.for('forms.resetpassword.errors.fields.email.invalid'),
            },
          })}
        />
        {errors.email && <p className="errorMessage">{errors.email.message}</p>}
      </div>

      {/* Password Field */}
      <div className="form-field">
        <label htmlFor="password">{translates.for('forms.resetpassword.fields.password.title')}:</label>
        <input
          type="password"
          id="password"
          placeholder={translates.for('forms.resetpassword.fields.password.placeholder')}
          {...register('password', {
            required: translates.for('forms.resetpassword.errors.fields.password.required'),
          })}
        />
        {errors.password && <p className="errorMessage">{errors.password.message}</p>}
      </div>

      {/* Password Repeat Field */}
      <div className="form-field">
        <label htmlFor="passwordRepeat">{translates.for('forms.resetpassword.fields.passwordRepeat.title')}:</label>
        <input
          type="password"
          id="passwordRepeat"
          placeholder={translates.for('forms.resetpassword.fields.passwordRepeat.placeholder')}
          {...register('passwordRepeat', {
            required: translates.for('forms.resetpassword.errors.fields.passwordRepeat.required'),
          })}
        />
        {errors.passwordRepeat && <p className="errorMessage">{errors.passwordRepeat.message}</p>}
      </div>

      <button className="submit-button" type="submit">
        {translates.for('forms.resetpassword.submit')}
      </button>
      {errors._general && <p className="errorMessage">{errors._general.message}</p>}
    </form>
  )
}